import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.js";
import { Badge, Button, Card, COLORS, Empty, Field, Form, Input, Page, Textarea } from "../ui.js";

interface Course {
  id: string;
  title: string;
  slug: string;
}

interface Offer {
  id: string;
  title: string;
  description: string | null;
  kind: "ONE_SHOT" | "SUBSCRIPTION";
  priceCents: number;
  currency: string;
  isActive: boolean;
  courseLinks: { courseId: string; course?: { id: string; title: string; slug: string } }[];
}

export default function Offers() {
  const qc = useQueryClient();
  const offers = useQuery({
    queryKey: ["offers"],
    queryFn: () => api.get<{ items: Offer[] }>("/offers"),
  });
  const courses = useQuery({
    queryKey: ["courses"],
    queryFn: () => api.get<{ items: Course[] }>("/courses"),
  });

  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("49");
  const [courseIds, setCourseIds] = useState<string[]>([]);

  const create = useMutation({
    mutationFn: () =>
      api.post<Offer>("/offers", {
        title,
        description,
        priceCents: Math.round(parseFloat(price) * 100),
        currency: "EUR",
        kind: "ONE_SHOT",
        courseIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setTitle("");
      setDescription("");
      setPrice("49");
      setCourseIds([]);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/offers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["offers"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  function toggleCourse(id: string) {
    setCourseIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  return (
    <Page title="Offres">
      <p style={{ color: COLORS.muted }}>
        Vos offres payantes. Chaque Offre débloque un ou plusieurs Cours après paiement.
      </p>

      {!creating ? (
        <Button onClick={() => setCreating(true)}>+ Nouvelle Offre</Button>
      ) : (
        <Card>
          <Form onSubmit={onSubmit}>
            <Field label="Titre">
              <Input required value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </Field>
            <Field label="Description">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Prix (€)">
              <Input
                type="number"
                step="0.01"
                min="0"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </Field>
            <Field label="Cours inclus">
              <div>
                {courses.data?.items.length === 0 && (
                  <p style={{ color: COLORS.muted, fontSize: 13 }}>
                    Aucun Cours. <Link to="/courses">Créez-en un d'abord</Link>.
                  </p>
                )}
                {courses.data?.items.map((c) => (
                  <label key={c.id} style={{ display: "block", padding: "4px 0", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={courseIds.includes(c.id)}
                      onChange={() => toggleCourse(c.id)}
                      style={{ marginRight: 8 }}
                    />
                    {c.title}
                  </label>
                ))}
              </div>
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Création…" : "Créer"}
              </Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Annuler
              </Button>
            </div>
          </Form>
        </Card>
      )}

      <div style={{ marginTop: 24 }}>
        {offers.data?.items.length === 0 && <Empty>Aucune Offre. Créez la première.</Empty>}
        {offers.data?.items.map((o) => (
          <OfferRow
            key={o.id}
            offer={o}
            allCourses={courses.data?.items ?? []}
            onDelete={() => del.mutate(o.id)}
          />
        ))}
      </div>
    </Page>
  );
}

// ─────────────────────────────────────────────────────────────
function OfferRow({
  offer,
  allCourses,
  onDelete,
}: {
  offer: Offer;
  allCourses: Course[];
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const [title, setTitle] = useState(offer.title);
  const [description, setDescription] = useState(offer.description ?? "");
  const [price, setPrice] = useState((offer.priceCents / 100).toString());
  const [isActive, setIsActive] = useState(offer.isActive);

  // courseLinks initiaux pour gérer link/unlink
  const initialCourseIds = offer.courseLinks.map((l) => l.courseId);
  const [courseIds, setCourseIds] = useState<string[]>(initialCourseIds);

  const updateMeta = useMutation({
    mutationFn: (patch: {
      title?: string;
      description?: string | null;
      priceCents?: number;
      isActive?: boolean;
    }) => api.patch<Offer>(`/offers/${offer.id}`, patch),
  });

  const link = useMutation({
    mutationFn: (courseId: string) => api.post(`/offers/${offer.id}/link`, { courseId }),
  });
  const unlink = useMutation({
    mutationFn: (courseId: string) => api.post(`/offers/${offer.id}/unlink`, { courseId }),
  });

  async function saveAll() {
    // 1. Patch meta
    await updateMeta.mutateAsync({
      title,
      description: description || null,
      priceCents: Math.round(parseFloat(price) * 100),
      isActive,
    });

    // 2. Calculer diff de courses
    const toLink = courseIds.filter((id) => !initialCourseIds.includes(id));
    const toUnlink = initialCourseIds.filter((id) => !courseIds.includes(id));
    for (const id of toLink) await link.mutateAsync(id);
    for (const id of toUnlink) await unlink.mutateAsync(id);

    qc.invalidateQueries({ queryKey: ["offers"] });
    setEditing(false);
  }

  if (!editing) {
    return (
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{offer.title}</div>
            {offer.description && (
              <div style={{ color: COLORS.muted, marginTop: 4 }}>{offer.description}</div>
            )}
            <div style={{ marginTop: 8 }}>
              <Badge kind={offer.isActive ? "ok" : "neutral"}>
                {offer.isActive ? "Active" : "Inactive"}
              </Badge>
              <span style={{ marginLeft: 12, fontWeight: 600 }}>
                {(offer.priceCents / 100).toFixed(2)} {offer.currency}
              </span>
            </div>
            <div style={{ marginTop: 8, fontSize: 13, color: COLORS.muted }}>
              {offer.courseLinks.length} Cours :{" "}
              {offer.courseLinks
                .map((l) => l.course?.title)
                .filter(Boolean)
                .join(", ") || "—"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={() => setEditing(true)} style={{ padding: "6px 14px", fontSize: 13 }}>
              Modifier
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm(`Supprimer "${offer.title}" ?`)) onDelete();
              }}
              style={{ padding: "6px 14px", fontSize: 13 }}
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  function toggleCourse(id: string) {
    setCourseIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  const pending = updateMeta.isPending || link.isPending || unlink.isPending;

  return (
    <Card>
      <Field label="Titre">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Description">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Prix (€)">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
      </Field>
      <Field label="Statut">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span style={{ fontSize: 14 }}>
            Offre active (visible et achetable)
          </span>
        </label>
      </Field>
      <Field label="Cours inclus">
        <div>
          {allCourses.map((c) => (
            <label key={c.id} style={{ display: "block", padding: "4px 0", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={courseIds.includes(c.id)}
                onChange={() => toggleCourse(c.id)}
                style={{ marginRight: 8 }}
              />
              {c.title}
            </label>
          ))}
        </div>
      </Field>
      <div style={{ display: "flex", gap: 8 }}>
        <Button onClick={saveAll} disabled={pending}>
          {pending ? "Enregistrement…" : "💾 Enregistrer"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setTitle(offer.title);
            setDescription(offer.description ?? "");
            setPrice((offer.priceCents / 100).toString());
            setIsActive(offer.isActive);
            setCourseIds(initialCourseIds);
            setEditing(false);
          }}
        >
          Annuler
        </Button>
      </div>
    </Card>
  );
}
